import { useLanguage, type Bilingual as BilingualType } from '../lib/LanguageContext';

interface BilingualProps extends Partial<BilingualType> {
  stack?: boolean;
  size?: 'default' | 'small' | 'large';
  className?: string;
  forceBilingual?: boolean;
}

/**
 * Bilingual component for displaying Indonesian + Arabic text.
 * Integrates with LanguageProvider context to render a clean localized UI.
 */
export function Bilingual({
  id = '',
  ar = '',
  stack = false,
  size = 'default',
  className = '',
  forceBilingual = false,
}: BilingualProps) {
  const { language } = useLanguage();

  const sizeClasses = {
    default: 'text-base',
    small: 'text-xs',
    large: 'text-2xl',
  };

  const idStr = typeof id === 'string' ? id : id ? String(id) : '';
  const arStr = typeof ar === 'string' ? ar : ar ? String(ar) : '';

  const hasId = idStr.trim().length > 0;
  const hasAr = arStr.trim().length > 0;

  // If both empty, render nothing
  if (!hasId && !hasAr) return null;

  // If forced bilingual or only one language is available, render using bilingual layout
  if (forceBilingual || !hasId || !hasAr) {
    // Arabic only (e.g., salam greeting)
    if (!hasId && hasAr) {
      return (
        <p
          lang="ar"
          dir="rtl"
          className={`font-arabic text-right text-on-surface-variant leading-[1.85] ${sizeClasses[size]} ${className}`}
        >
          {arStr}
        </p>
      );
    }

    // Indonesian only
    if (hasId && !hasAr) {
      return <span className={`font-headline ${className}`}>{idStr}</span>;
    }

    // Both present and bilingual requested
    if (stack) {
      return (
        <div className={`flex flex-col gap-1.5 ${className}`}>
          <span className="font-headline">{idStr}</span>
          <span
            lang="ar"
            dir="rtl"
            className={`font-arabic text-right leading-[1.85] ${sizeClasses[size]}`}
          >
            {arStr}
          </span>
        </div>
      );
    }

    return (
      <div className={`flex items-baseline gap-2 ${className}`}>
        <span className="font-headline">{idStr}</span>
        <span>/</span>
        <span lang="ar" dir="rtl" className="font-arabic text-sm">
          {arStr}
        </span>
      </div>
    );
  }

  // Normal localized rendering (single language view)
  if (language === 'ar') {
    return (
      <span
        lang="ar"
        dir="rtl"
        className={`font-arabic leading-[1.85] ${sizeClasses[size]} ${className}`}
      >
        {arStr}
      </span>
    );
  }

  return <span className={`font-headline ${className}`}>{idStr}</span>;
}
